/* @flow */
import { getApolloClientSingleton } from 'lib/apollo/client/browser';
import { Map } from 'immutable';
import { omit } from 'lodash';
import mapValuesDeep from 'map-values-deep';
import gql from 'graphql-tag';
import idx from 'idx';
import Cookies from 'lib/Cookies';
import CurrentUser from 'auth/CurrentUser';

export const PAGE_NUMBER = 3;
export const STUDENT_COUNT_FOR_TWO_DAY_FEATURES = 250;
export const HIGH_TOUCH_STUDENT_COUNT = 2500;
export const NO_TOUCH_STUDENT_COUNT_MAX = 249;

export type Fields = Map<string, *>;

export type State = {
  pageIndex: number,
  fields: Fields,
  quote?: *,
  startUrl: ?string,
  paymentMethod: 'card' | 'invoice',
  cardValid: boolean,
};

export const initialState: State = {
  pageIndex: 0,
  fields: Map(),
  quote: undefined,
  startUrl: undefined,
  paymentMethod: 'card',
  cardValid: false,
};

function boundedPage(page: number): number {
  if (page >= PAGE_NUMBER - 1) {
    return PAGE_NUMBER - 1;
  }

  if (page <= 0) {
    return 0;
  }

  return page;
}

export const ORG_TYPE_STRING = {
  school: 'School',
  district: 'District',
  other: 'Organization',
};

/* ======= */
/* REDUCER */
/* ======= */

export default function reducer(state: State = initialState, action: *): State {
  switch (action.type) {
    case 'adminUpgradePage/NEXT_PAGE': {
      return { ...state, pageIndex: boundedPage(state.pageIndex + 1) };
    }
    case 'adminUpgradePage/PREVIOUS_PAGE': {
      return { ...state, pageIndex: boundedPage(state.pageIndex - 1) };
    }
    case 'adminUpgradePage/REINITIALIZE': {
      return { ...initialState };
    }
    case 'adminUpgradePage/SAVE_FIELD': {
      return {
        ...state,
        fields: state.fields.set(action.name, action.value),
      };
    }
    case 'adminUpgradePage/SAVE_QUOTE_TO_FIELDS': {
      const { shareToken, startDate, organization, organizationAddress } = action.quote;
      const parsedQuote = {
        ...action.quote,
        startDate: startDate ? new Date(startDate) : null,
        orgType: organization ? organization.type.toLowerCase() : state.fields.get('orgType'),
        organizationAddress: omit(organizationAddress, '__typename'),
        shareToken: shareToken || null,
      };
      return { ...state, fields: Map(parsedQuote) };
    }
    case 'adminUpgradePage/SAVE_QUOTE': {
      return { ...state, quote: action.quote };
    }
    case 'adminUpgradePage/SAVE_START_URL': {
      return { ...state, startUrl: action.url };
    }
    case 'adminUpgradePage/SET_PAYMENT_METHOD': {
      return { ...state, paymentMethod: action.paymentMethod };
    }
    case 'adminUpgradePage/HANDLE_CARD_VALIDITY_CHANGE': {
      return { ...state, cardValid: action.valid };
    }
    default:
      return state;
  }
}

/* ======= */
/* ACTIONS */
/* ======= */

export type GoNextPageAction = { type: 'adminUpgradePage/NEXT_PAGE' };
export function goNextPage(): GoNextPageAction {
  return { type: 'adminUpgradePage/NEXT_PAGE' };
}

export type GoPreviousPageAction = { type: 'adminUpgradePage/PREVIOUS_PAGE' };
export function goPreviousPage(): GoPreviousPageAction {
  return { type: 'adminUpgradePage/PREVIOUS_PAGE' };
}

export type ReinitializeAction = { type: 'adminUpgradePage/REINITIALIZE' };
export function reinitialize(): ReinitializeAction {
  return { type: 'adminUpgradePage/REINITIALIZE' };
}

export type saveLocalQuoteFieldAction = {
  type: 'adminUpgradePage/SAVE_FIELD',
  name: string,
  value: *,
};

export function saveLocalQuoteField(name: string, value: *): saveLocalQuoteFieldAction {
  return {
    type: 'adminUpgradePage/SAVE_FIELD',
    value,
    name,
  };
}

export type SaveQuoteToFieldsAction = {
  type: 'adminUpgradePage/SAVE_QUOTE_TO_FIELDS',
  quote: *,
};

export function saveQuoteToFields(quote: *): SaveQuoteToFieldsAction {
  return {
    type: 'adminUpgradePage/SAVE_QUOTE_TO_FIELDS',
    quote,
  };
}

export type SaveQuoteAction = {
  type: 'adminUpgradePage/SAVE_QUOTE',
  quote: *,
};

export function saveQuote(quote: Object): SaveQuoteAction {
  return {
    type: 'adminUpgradePage/SAVE_QUOTE',
    quote,
  };
}
export type SaveStartUrl = {
  type: 'adminUpgradePage/SAVE_START_URL',
  url: ?string,
};

export function saveStartUrl(url: ?string): SaveStartUrl {
  return {
    type: 'adminUpgradePage/SAVE_START_URL',
    url,
  };
}

export type SetPaymentMethod = {
  type: 'adminUpgradePage/SET_PAYMENT_METHOD',
  paymentMethod: string,
};

export function setPaymentMethod(paymentMethod: string): SetPaymentMethod {
  return {
    type: 'adminUpgradePage/SET_PAYMENT_METHOD',
    paymentMethod,
  };
}

export type HandleCardValidityChange = {
  type: 'adminUpgradePage/HANDLE_CARD_VALIDITY_CHANGE',
  valid: boolean,
};

export function handleCardValidityChange(valid: boolean): HandleCardValidityChange {
  return {
    type: 'adminUpgradePage/HANDLE_CARD_VALIDITY_CHANGE',
    valid,
  };
}

const CREATE_MUTATION = gql`
  mutation CreateQuote($input: QuoteInput!) {
    createQuote(input: $input) {
      quote {
        uuid
        studentsCount
        teachersCount
        startDate
        endDate
        taxExempt
        purchaseOrderNumber
        price
        tax
        total
        expirationDate
        shareToken
        organization {
          id
          uuid
          name
          city
          state
        }
        organizationAddress {
          latitude
          longitude
          streetAddress
          city
          state
          country
          postalCode
        }
      }
      error {
        message
        code
        fieldErrors {
          field
          message
        }
      }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation updateQuote($uuid: String!, $input: QuoteInput!) {
    updateQuote(uuid: $uuid, input: $input) {
      quote {
        uuid
        studentsCount
        teachersCount
        startDate
        endDate
        taxExempt
        purchaseOrderNumber
        price
        tax
        total
        expirationDate
        shareToken
        organization {
          id
          uuid
          name
          city
          state
        }
        organizationAddress {
          streetAddress
          city
          state
          country
          postalCode
        }
      }
      error {
        message
        code
        fieldErrors {
          field
          message
        }
      }
    }
  }
`;

export function conditionallyAddPlus(number: ?string) {
  if (number && number.length > 0 && !number.startsWith('+')) {
    return '+' + number;
  } else {
    return number;
  }
}

export function getMarketingExperimentFields(): {
  experiment?: string,
  experimentVariant?: string,
} {
  const experimentVariant = Cookies.get('marketing_experiment_variant');
  if (experimentVariant) {
    return {
      experiment: '2018.oct.web.cms_backed_marketing_site',
      experimentVariant,
    };
  }

  return {};
}

export function saveQuoteToBackend(quoteUUID?: ?string, fields: Fields, pageIndex: number): any {
  const trimWhitespaceOnStrings = field => {
    if (typeof field === 'string' || field instanceof String) {
      return field.trim();
    }
    return field;
  };

  return dispatch => {
    const mutationInput = mapValuesDeep(
      {
        organizationUuid: fields.get('organizationUuid'),
        studentsCount: fields.get('studentsCount'),
        teachersCount: fields.get('teachersCount'),
        startDate: fields.get('startDate'),
        billingFirstName: fields.get('billingFirstName'),
        billingLastName: fields.get('billingLastName'),
        billingEmail: fields.get('billingEmail'),
        billingPhoneNumber: conditionallyAddPlus(fields.get('billingPhoneNumber')),
        taxExempt: fields.get('taxExempt'),
        purchaseOrderNumber: fields.get('purchaseOrderNumber'),
        requesterFirstName: fields.get('requesterFirstName'),
        requesterLastName: fields.get('requesterLastName'),
        requesterEmail: fields.get('requesterEmail'),
        requesterPhoneNumber: conditionallyAddPlus(fields.get('requesterPhoneNumber')),
        requesterRole: fields.get('requesterRole'),
        organizationAddress: {
          latitude: fields.get('latitude') || null,
          longitude: fields.get('longitude') || null,
          streetAddress: fields.get('streetAddress'),
          city: fields.get('city'),
          state: fields.get('state'),
          country: fields.get('country'),
          postalCode: fields.get('postalCode'),
        },
        utmMedium: fields.get('utmMedium'),
        utmSource: fields.get('utmSource'),
        utmContent: fields.get('utmContent'),
        utmCampaign: fields.get('utmCampaign'),
        lastScreenName: `Screen ${pageIndex + 1}`,
        ...getMarketingExperimentFields(),
      },
      trimWhitespaceOnStrings
    );

    let promise;

    if (quoteUUID) {
      promise = getApolloClientSingleton().mutate({
        mutation: UPDATE_MUTATION,
        variables: {
          uuid: quoteUUID,
          input: mutationInput,
        },
      });
    } else {
      promise = getApolloClientSingleton().mutate({
        mutation: CREATE_MUTATION,
        variables: {
          input: mutationInput,
        },
      });
    }

    return promise.then(results => {
      const quote =
        idx(results, _ => _.data.createQuote.quote) || idx(results, _ => _.data.updateQuote.quote);

      const error =
        idx(results, _ => _.data.createQuote.error) || idx(results, _ => _.data.updateQuote.error);

      if (quote) {
        dispatch(saveQuote(quote));
      }

      return {
        quote,
        error,
      };
    });
  };
}

export type Actions =
  | GoNextPageAction
  | GoPreviousPageAction
  | ReinitializeAction
  | SaveQuoteAction;

/* =================== */
/* STATE READER HELPER */
/* =================== */

type ButtonState = {
  text: string,
  hidden: boolean,
  disabled: boolean,
};

export function nextButtonState(state: State): ButtonState {
  const disabled = !isPageValid(state, state.pageIndex);
  const buttonState = { text: 'Next', hidden: false, disabled };

  switch (state.pageIndex) {
    case 0:
      return { ...buttonState, text: 'Continue' };
    case 1:
      return { ...buttonState, text: 'Continue' };
    case 2:
      return { ...buttonState, text: 'Purchase' };
    default:
      return { ...buttonState, hidden: true };
  }
}

export function previousButtonState(state: State): ButtonState {
  const buttonState = { text: 'Back', hidden: false, disabled: false };

  switch (state.pageIndex) {
    case 0:
      return { ...buttonState, hidden: true };
    case 1:
      return buttonState;
    case 2:
      return buttonState;
    default:
      return { ...buttonState, hidden: true };
  }
}

function truthy(value?: ?string): boolean {
  if (typeof value === 'string' || value instanceof String) {
    return value.length > 0;
  }

  return !!value;
}

function isPageValid(state: State, pageIndex: number): boolean {
  switch (pageIndex) {
    case 0:
      return (
        truthy(state.fields.get('organizationUuid')) &&
        truthy(state.fields.get('studentsCount')) &&
        truthy(state.fields.get('teachersCount')) &&
        truthy(state.fields.get('startDate')) &&
        truthy(state.fields.get('requesterPhoneNumber')) &&
        truthy(state.fields.get('requesterRole')) &&
        truthy(state.fields.get('streetAddress')) &&
        truthy(state.fields.get('city')) &&
        truthy(state.fields.get('state')) &&
        truthy(state.fields.get('country')) &&
        truthy(state.fields.get('postalCode')) &&
        (CurrentUser.isSignedIn() ||
          (truthy(state.fields.get('requesterEmail')) &&
            truthy(state.fields.get('requesterFirstName')) &&
            truthy(state.fields.get('requesterLastName'))))
      );
    case 1:
      return true;
    case 2:
      return (
        truthy(state.fields.get('billingFirstName')) &&
        truthy(state.fields.get('billingLastName')) &&
        truthy(state.fields.get('billingEmail')) &&
        truthy(state.fields.get('billingPhoneNumber')) &&
        (state.paymentMethod === 'invoice' || state.cardValid)
      );
    default:
      return false;
  }
}
